import React, { useState, useEffect } from 'react';
import { ArrowLeft, RefreshCw, Mail, Phone, MessageSquare, Calendar, CheckCircle, Eye, Reply, LogOut, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchInquiries();
  }, [navigate]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/inquiries`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setInquiries(response.data);
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      if (err.response?.status === 401) {
        // Token invalid or expired
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        setError('Failed to load inquiries');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/admin/inquiries/${inquiryId}/status`, 
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      // Refresh inquiries list
      await fetchInquiries();
      // Update selected inquiry if it's the one being updated
      if (selectedInquiry && selectedInquiry.id === inquiryId) {
        const updated = await axios.get(`${API}/admin/inquiries/${inquiryId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSelectedInquiry(updated.data);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        alert('Failed to update status');
      }
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/inquiries/${inquiryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Clear selected inquiry if it was the deleted one
      if (selectedInquiry && selectedInquiry.id === inquiryId) {
        setSelectedInquiry(null);
      }
      
      // Refresh inquiries list
      await fetchInquiries();
      
      alert('Inquiry deleted successfully');
    } catch (err) {
      console.error('Error deleting inquiry:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        alert('Failed to delete inquiry');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'new':
        return 'var(--accent-primary)';
      case 'read':
        return '#FFA726';
      case 'replied':
        return 'var(--accent-text)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      {/* Header */}
      <header
        style={{
          background: 'white',
          borderBottom: '1px solid var(--border-light)',
          padding: '1.5rem',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = 'var(--bg-section)'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="heading-3">Admin Panel - Contact Inquiries</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={fetchInquiries}
              className="btn-secondary"
              style={{ gap: '0.5rem' }}
              disabled={loading}
            >
              <RefreshCw size={18} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="btn-secondary"
              style={{ gap: '0.5rem' }}
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container" style={{ padding: '2rem 1.5rem' }}>
        {error && (
          <div
            style={{
              background: '#FEE',
              border: '1px solid #FCC',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              color: '#C33'
            }}
          >
            {error}
          </div>
        )}

        {loading && inquiries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
            <RefreshCw size={48} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
            <p>Loading inquiries...</p>
          </div>
        ) : inquiries.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '3rem',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid var(--border-light)'
            }}
          >
            <MessageSquare size={48} style={{ color: 'var(--text-secondary)', margin: '0 auto 1rem' }} />
            <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>No inquiries yet</h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>Contact form submissions will appear here</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: selectedInquiry ? '1fr 1.5fr' : '1fr', gap: '1.5rem' }}>
            {/* Inquiries List */}
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Total: {inquiries.length} {inquiries.length === 1 ? 'inquiry' : 'inquiries'}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {inquiries.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    onClick={() => setSelectedInquiry(inquiry)}
                    style={{
                      background: selectedInquiry?.id === inquiry.id ? 'var(--accent-wash)' : 'white',
                      border: `1px solid ${selectedInquiry?.id === inquiry.id ? 'var(--accent-primary)' : 'var(--border-light)'}`,
                      borderRadius: '12px',
                      padding: '1.25rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedInquiry?.id !== inquiry.id) {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                      <div>
                        <h4 style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{inquiry.name}</h4>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{inquiry.email}</p>
                      </div>
                      <span
                        style={{
                          background: getStatusBadgeColor(inquiry.status),
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}
                      >
                        {inquiry.status}
                      </span>
                    </div>
                    <p
                      className="body-small"
                      style={{
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {inquiry.message}
                    </p>
                    <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                      {formatDate(inquiry.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Inquiry Details */}
            {selectedInquiry && (
              <div
                style={{
                  background: 'white',
                  border: '1px solid var(--border-light)',
                  borderRadius: '12px',
                  padding: '2rem',
                  position: 'sticky',
                  top: '120px',
                  maxHeight: 'calc(100vh - 140px)',
                  overflowY: 'auto'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                  <h3 className="heading-3">Inquiry Details</h3>
                  <span
                    style={{
                      background: getStatusBadgeColor(selectedInquiry.status),
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: '16px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}
                  >
                    {selectedInquiry.status}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                      <Mail size={16} />
                      <span className="body-small" style={{ fontWeight: '600' }}>Email</span>
                    </div>
                    <p className="body-medium">{selectedInquiry.email}</p>
                  </div>

                  {selectedInquiry.phone && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                        <Phone size={16} />
                        <span className="body-small" style={{ fontWeight: '600' }}>Phone</span>
                      </div>
                      <p className="body-medium">{selectedInquiry.phone}</p>
                    </div>
                  )}

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                      <Calendar size={16} />
                      <span className="body-small" style={{ fontWeight: '600' }}>Received</span>
                    </div>
                    <p className="body-medium">{formatDate(selectedInquiry.created_at)}</p>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                      <MessageSquare size={16} />
                      <span className="body-small" style={{ fontWeight: '600' }}>Message</span>
                    </div>
                    <div
                      style={{
                        background: 'var(--bg-section)',
                        padding: '1rem',
                        borderRadius: '8px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      <p className="body-medium">{selectedInquiry.message}</p>
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '1.5rem' }}>
                  <p className="body-small" style={{ fontWeight: '600', marginBottom: '1rem', color: 'var(--text-secondary)' }}>Update Status</p>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => updateInquiryStatus(selectedInquiry.id, 'new')}
                      disabled={selectedInquiry.status === 'new'}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-light)',
                        background: selectedInquiry.status === 'new' ? 'var(--accent-wash)' : 'white',
                        cursor: selectedInquiry.status === 'new' ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedInquiry.status !== 'new') {
                          e.target.style.background = 'var(--bg-section)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedInquiry.status !== 'new') {
                          e.target.style.background = 'white';
                        }
                      }}
                    >
                      {selectedInquiry.status === 'new' && <CheckCircle size={16} />}
                      New
                    </button>
                    <button
                      onClick={() => updateInquiryStatus(selectedInquiry.id, 'read')}
                      disabled={selectedInquiry.status === 'read'}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-light)',
                        background: selectedInquiry.status === 'read' ? 'var(--accent-wash)' : 'white',
                        cursor: selectedInquiry.status === 'read' ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedInquiry.status !== 'read') {
                          e.target.style.background = 'var(--bg-section)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedInquiry.status !== 'read') {
                          e.target.style.background = 'white';
                        }
                      }}
                    >
                      {selectedInquiry.status === 'read' && <CheckCircle size={16} />}
                      <Eye size={16} />
                      Read
                    </button>
                    <button
                      onClick={() => updateInquiryStatus(selectedInquiry.id, 'replied')}
                      disabled={selectedInquiry.status === 'replied'}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-light)',
                        background: selectedInquiry.status === 'replied' ? 'var(--accent-wash)' : 'white',
                        cursor: selectedInquiry.status === 'replied' ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedInquiry.status !== 'replied') {
                          e.target.style.background = 'var(--bg-section)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedInquiry.status !== 'replied') {
                          e.target.style.background = 'white';
                        }
                      }}
                    >
                      {selectedInquiry.status === 'replied' && <CheckCircle size={16} />}
                      <Reply size={16} />
                      Replied
                    </button>
                  </div>
                </div>

                {/* Delete Button */}
                <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                  <button
                    onClick={() => deleteInquiry(selectedInquiry.id)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid #FCC',
                      background: 'white',
                      color: '#C33',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#FEE';
                      e.target.style.borderColor = '#C33';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.borderColor = '#FCC';
                    }}
                  >
                    <Trash2 size={16} />
                    Delete Inquiry
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 1024px) {
          /* Stack layout on tablets and mobile */
          div[style*="grid-template-columns: 1fr 1.5fr"] {
            grid-template-columns: 1fr !important;
          }
          
          /* Remove sticky positioning on mobile */
          div[style*="position: sticky"][style*="top: 120px"] {
            position: static !important;
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;